import React, { useEffect, useState } from "react";
import api from "../../utils/api";

const formatTime = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit", hour12: false, timeZone: "Europe/London" });
};

const formatDate = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleDateString("en-GB", { weekday: "short", day: "numeric", month: "short", year: "numeric", timeZone: "Europe/London" });
};

const formatDuration = (mins) => {
  const n = Number(mins);
  if (!Number.isFinite(n) || n <= 0) return "—";
  const h = Math.floor(n / 60);
  const m = Math.round(n % 60);
  if (h <= 0) return `${m} mins`;
  if (m === 0) return `${h}h`;
  return `${h}h ${m}m`;
};

const opsApiRequest = async (url) => {
  const res = await api.request({ url, method: "GET" });
  return res?.data;
};

const opsStatusColor = (level) => {
  const key = String(level || "unknown").toLowerCase();
  if (key === "green") return { bg: "#ecfdf5", border: "#86efac", text: "#166534" };
  if (key === "light_green") return { bg: "#f0fdf4", border: "#bbf7d0", text: "#166534" };
  if (key === "amber") return { bg: "#fffbeb", border: "#fcd34d", text: "#b45309" };
  if (key === "red" || key === "critical_red") return { bg: "#fef2f2", border: "#fca5a5", text: "#b91c1c" };
  return { bg: "#f8fafc", border: "#cbd5e1", text: "#475569" };
};

const opsBadge = (level) => {
  const palette = opsStatusColor(level);
  return {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    padding: "4px 10px",
    borderRadius: 999,
    border: `1px solid ${palette.border}`,
    background: palette.bg,
    color: palette.text,
    fontSize: 11,
    fontWeight: 800,
    letterSpacing: 0.35,
    textTransform: "uppercase",
    whiteSpace: "nowrap",
  };
};

const cardTheme = {
  road: {
    border: "#93c5fd",
    bg: "linear-gradient(180deg, #eaf4ff 0%, #f8fbff 28%, #ffffff 100%)",
    accent: "#2563eb",
  },
  rail: {
    border: "#c4b5fd",
    bg: "linear-gradient(180deg, #f2edff 0%, #fbf9ff 28%, #ffffff 100%)",
    accent: "#4338ca",
  },
  flights: {
    border: "#f9a8d4",
    bg: "linear-gradient(180deg, #fff0f7 0%, #fff8fc 28%, #ffffff 100%)",
    accent: "#db2777",
  },
};

const opsCardStyle = (theme) => ({
  border: `1.5px solid ${theme.border}`,
  borderRadius: 18,
  background: theme.bg,
  padding: 16,
  boxShadow: "0 12px 32px rgba(15, 23, 42, 0.07)",
});

const opsMiniLabel = {
  fontSize: 11,
  fontWeight: 800,
  letterSpacing: 0.35,
  textTransform: "uppercase",
  color: "#64748b",
};

const asArray = (value) => (Array.isArray(value) ? value : []);

const formatRoadDeltaMins = (secs) => {
  const n = Number(secs);
  if (!Number.isFinite(n)) return "vs usual N/A";
  const mins = Math.round(n / 60);
  if (mins === 0) return "on usual time";
  return `${mins > 0 ? "+" : ""}${mins}m vs usual`;
};

const formatMiles = (meters) => {
  const n = Number(meters);
  if (!Number.isFinite(n)) return "—";
  return `${(n / 1609.344).toFixed(1)} mi`;
};

const formatFlightLine = (flight = {}, type = "movement") => {
  const routeCode =
    type === "arrival"
      ? flight?.origin_airport_code || flight?.origin_airport_name || "—"
      : flight?.destination_airport_code || flight?.destination_airport_name || "—";
  const when = flight?.actual_utc || flight?.estimated_utc || flight?.scheduled_utc;
  const extra = [];
  if (flight?.delay_mins != null && Number(flight.delay_mins) > 0) extra.push(`+${Math.round(Number(flight.delay_mins))}m`);
  if (flight?.gate_name || flight?.gate_number) extra.push(flight.gate_name || `Gate ${flight.gate_number}`);
  if (flight?.baggage_carousel_number) extra.push(`Belt ${flight.baggage_carousel_number}`);
  return `${formatTime(when)} • ${flight?.flight_number || "—"} • ${routeCode}${extra.length ? ` • ${extra.join(" • ")}` : ""}`;
};

const formatFlightScheduleLine = (flight = {}, type = "movement") => {
  const routeCode =
    type === "arrival"
      ? flight?.origin_airport_code || flight?.origin_airport_name || "—"
      : flight?.destination_airport_code || flight?.destination_airport_name || "—";
  return `${formatTime(flight?.scheduled_utc)} • ${flight?.flight_number || "—"} • ${routeCode}`;
};

const formatFlightUpcomingLine = (flight = {}, type = "movement") => {
  const routeCode =
    type === "arrival"
      ? flight?.origin_airport_code || flight?.origin_airport_name || "—"
      : flight?.destination_airport_code || flight?.destination_airport_name || "—";
  const when = flight?.estimated_utc || flight?.scheduled_utc || flight?.actual_utc;
  const extra = [];
  if (flight?.delay_mins != null && Number(flight.delay_mins) > 0) extra.push(`+${Math.round(Number(flight.delay_mins))}m`);
  if (flight?.gate_name || flight?.gate_number) extra.push(flight.gate_name || `Gate ${flight.gate_number}`);
  if (flight?.baggage_carousel_number) extra.push(`Belt ${flight.baggage_carousel_number}`);
  return `${formatTime(when)} • ${flight?.flight_number || "—"} • ${routeCode}${extra.length ? ` • ${extra.join(" • ")}` : ""}`;
};

const formatRailIssueLine = (today = {}) => {
  const delayed = Number(today?.delayed_runs_today || 0);
  const cancelled = Number(today?.cancelled_runs_today || 0);
  if (delayed === 0 && cancelled === 0) return "Today: no rail disruption seen";
  return `Today: delayed ${delayed} • cancelled ${cancelled}${today?.last_issue_at ? ` • last issue ${formatTime(today.last_issue_at)}` : ""}`;
};

const formatRailNextLine = (board = {}) => {
  const nextService =
    asArray(board?.departures)[0] ||
    asArray(board?.arrivals)[0] ||
    asArray(board?.relevant_services)[0] ||
    null;
  if (!nextService) return "No live board service";
  const timeText =
    nextService?.std_local ||
    nextService?.expected_time_local ||
    nextService?.departure_scheduled_local ||
    nextService?.arrival_scheduled_local ||
    "Live";
  const context = String(nextService?.context_summary || "").trim();
  const statusText = String(nextService?.status_text || "").trim();
  const expected = String(nextService?.expected_time_local || nextService?.departure_expected_local || nextService?.arrival_expected_local || "").trim();
  const parts = [timeText];
  if (context) parts.push(context);
  if (statusText && !["on time", timeText.toLowerCase()].includes(statusText.toLowerCase())) {
    parts.push(/^\d{2}:\d{2}$/.test(statusText) ? `exp ${statusText}` : statusText);
  } else if (expected && /^\d{2}:\d{2}$/.test(expected) && expected !== timeText) {
    parts.push(`exp ${expected}`);
  } else if (statusText && statusText.toLowerCase() === "on time") {
    parts.push("On time");
  }
  return parts.join(" • ");
};

const findRailBoard = (boards = [], keyPart) =>
  asArray(boards).find((row) => String(row?.target?.target_key || "").toLowerCase().includes(String(keyPart || "").toLowerCase())) || null;

const railBoardSummary = (board) => {
  if (!board) return null;
  const status = String(board?.board?.status_label || board?.target?.effective_status_label || "unknown").toLowerCase();
  const counts = [];
  if (Number(board?.board?.cancelled_count || 0) > 0) counts.push(`${Number(board.board.cancelled_count)} cancelled`);
  if (Number(board?.board?.delayed_count || 0) > 0) counts.push(`${Number(board.board.delayed_count)} delayed`);
  if (!counts.length) counts.push("Good service");
  return {
    label: board?.target?.display_name || board?.target?.target_key || "Station",
    target_key: board?.target?.target_key || null,
    status,
    summary: counts.join(" • "),
    next: formatRailNextLine(board),
  };
};

export default function EmaLiveOpsSection() {
  const [road, setRoad] = useState({ corridors: [], details: {}, today: {}, loading: true, error: "" });
  const [rail, setRail] = useState({ boards: null, loading: true, error: "" });
  const [flights, setFlights] = useState({ snapshot: null, loading: true, error: "" });

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      try {
        setRoad((prev) => ({ ...prev, loading: true, error: "" }));
        setRail((prev) => ({ ...prev, loading: true, error: "" }));
        setFlights((prev) => ({ ...prev, loading: true, error: "" }));

        const summary = await opsApiRequest("/api/open/ema/live-ops-summary");
        if (cancelled) return;

        setRoad({
          corridors: asArray(summary?.road?.corridors),
          details: summary?.road?.details || {},
          today: summary?.road?.today || {},
          loading: false,
          error: "",
        });
        setRail({ boards: summary?.rail || null, loading: false, error: "" });
        setFlights({ snapshot: summary?.flights || null, loading: false, error: "" });
      } catch (err) {
        if (cancelled) return;
        const message = err?.response?.data?.error || err?.message || "Unable to load live EMA status.";
        setRoad((prev) => ({ ...prev, loading: false, error: message }));
        setRail((prev) => ({ ...prev, loading: false, error: message }));
        setFlights((prev) => ({ ...prev, loading: false, error: message }));
      }
    };

    load();
    const timer = setInterval(load, 5 * 60 * 1000);
    return () => {
      cancelled = true;
      clearInterval(timer);
    };
  }, []);

  const roadRows = road.corridors.map((corridor) => {
    const detail = road.details[corridor.corridor_key] || {};
    const segments = asArray(detail?.segments);
    const abnormalSegments = segments.filter((segment) => ["amber", "red", "critical_red"].includes(String(segment?.status_label || "").toLowerCase()));
    return { corridor, segments, abnormalSegments };
  });

  const railBoards = rail.boards || {};
  const primaryRailRows = [
    railBoardSummary(findRailBoard(railBoards.station_boards, "emd")),
    railBoardSummary(findRailBoard(railBoards.gateway_boards, "stp")),
    railBoardSummary(findRailBoard(railBoards.gateway_boards, "bhm")),
  ].filter(Boolean);
  const extraRailRows = [
    railBoardSummary(findRailBoard(railBoards.gateway_boards, "dby")),
    railBoardSummary(findRailBoard(railBoards.gateway_boards, "lge")),
    railBoardSummary(findRailBoard(railBoards.gateway_boards, "not")),
  ].filter(Boolean);

  const flightSnapshot = flights.snapshot || {};
  const recentArrival = asArray(flightSnapshot.latest_arrivals)[0] || null;
  const recentDeparture = asArray(flightSnapshot.latest_departures)[0] || null;
  const nextArrival = flightSnapshot?.next_live_arrival || null;
  const nextDeparture = flightSnapshot?.next_live_departure || null;
  const fallbackNextArrival = flightSnapshot?.next_scheduled_arrival || null;
  const fallbackNextDeparture = flightSnapshot?.next_scheduled_departure || null;
  const railToday = railBoards?.today || {};
  const weather = flightSnapshot?.weather || null;

  return (
    <section style={{ marginTop: 18, padding: "18px 0 0" }}>
      <div style={{ display: "grid", gap: 10 }}>
        <div>
          <div style={{ fontSize: 12, fontWeight: 800, letterSpacing: 0.35, textTransform: "uppercase", color: "#2563eb" }}>
            Live EMA Status
          </div>
          <h2 style={{ margin: "6px 0 4px", fontSize: 24, lineHeight: 1.15, color: "#0f172a" }}>
            Road, Rail and Flight live status
          </h2>
          <div style={{ fontSize: 14, color: "#475569", maxWidth: 900 }}>
            This uses the same live corridor, station-board and flight monitoring already running behind the EMA dashboard, but in a compact customer view.
          </div>
        </div>

        <div style={{ display: "grid", gap: 12, gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))" }}>
          <div style={opsCardStyle(cardTheme.road)}>
            <div style={{ display: "flex", justifyContent: "space-between", gap: 10, alignItems: "center", marginBottom: 10 }}>
              <div>
                <div style={{ ...opsMiniLabel, color: cardTheme.road.accent }}>Road to EMA</div>
                <div style={{ fontSize: 18, fontWeight: 800, color: "#0f172a" }}>M1 corridors</div>
              </div>
              <span style={opsBadge(roadRows.some((row) => ["red", "critical_red"].includes(String(row.corridor?.status_label || "").toLowerCase())) ? "red" : roadRows.some((row) => String(row.corridor?.status_label || "").toLowerCase() === "amber") ? "amber" : "green")}>
                {road.loading ? "Loading" : "Live"}
              </span>
            </div>
            {road.error ? <div style={{ color: "#b91c1c", fontSize: 13 }}>{road.error}</div> : null}
            {!road.error && roadRows.map(({ corridor, segments, abnormalSegments }) => {
              const today = road.today?.[corridor.corridor_key] || null;
              return (
                <details key={corridor.corridor_key} style={{ border: "1px solid #e2e8f0", borderRadius: 14, padding: "10px 12px", marginBottom: 10 }} open>
                  <summary style={{ cursor: "pointer", listStyle: "none" }}>
                    <div style={{ display: "flex", justifyContent: "space-between", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
                      <div style={{ fontWeight: 700, color: "#0f172a" }}>{corridor.display_name || corridor.corridor_key}</div>
                      <span style={opsBadge(corridor.status_label)}>{String(corridor.status_label || "unknown").toUpperCase()}</span>
                    </div>
                    <div style={{ marginTop: 6, fontSize: 13, color: "#334155" }}>
                      {`${formatDuration(Number(corridor.route_duration_live_sec || 0) / 60)} • ${formatMiles(corridor.route_distance_m)} • ${formatRoadDeltaMins(corridor.route_delta_sec)}`}
                    </div>
                  </summary>
                  <div style={{ marginTop: 10, display: "grid", gap: 6, fontSize: 13, color: "#475569" }}>
                    {abnormalSegments.length ? (
                      abnormalSegments.map((segment) => (
                        <div key={segment.segment_key} style={{ display: "flex", justifyContent: "space-between", gap: 10, flexWrap: "wrap" }}>
                          <span>{segment.display_label}</span>
                          <span>{`${formatDuration(Number(segment.duration_live_sec || 0) / 60)} • ${formatMiles(segment.distance_m)} • ${formatRoadDeltaMins(segment.delta_sec)}`}</span>
                        </div>
                      ))
                    ) : (
                      <div>Route broadly normal. Tap again later if you want to check each motorway segment in more detail.</div>
                    )}
                    {today?.worst_segment_key ? (
                      <div style={{ fontSize: 12, color: "#64748b" }}>
                        {`Today worst over-baseline: ${today.worst_segment_label} • ${formatRoadDeltaMins(today.worst_segment_delta_sec)} • ${formatTime(today.worst_segment_observed_at_utc)}`}
                      </div>
                    ) : null}
                    <details style={{ marginTop: 6 }} open={abnormalSegments.length > 0}>
                      <summary style={{ cursor: "pointer", color: "#2563eb", fontWeight: 700 }}>Show full segment detail</summary>
                      <div style={{ marginTop: 8, display: "grid", gap: 4 }}>
                        {segments.map((segment) => (
                          <div key={segment.segment_key} style={{ display: "flex", justifyContent: "space-between", gap: 10, flexWrap: "wrap" }}>
                            <span>{`${segment.display_label} • ${String(segment.status_label || "unknown").toUpperCase()}`}</span>
                            <span>{`${formatDuration(Number(segment.duration_live_sec || 0) / 60)} • ${formatMiles(segment.distance_m)}`}</span>
                          </div>
                        ))}
                      </div>
                    </details>
                  </div>
                </details>
              );
            })}
            {!road.error && !roadRows.length && <div style={{ color: "#64748b", fontSize: 13 }}>No motorway corridor status available yet.</div>}
          </div>

          <div style={opsCardStyle(cardTheme.rail)}>
            <div style={{ display: "flex", justifyContent: "space-between", gap: 10, alignItems: "center", marginBottom: 10 }}>
              <div>
                <div style={{ ...opsMiniLabel, color: cardTheme.rail.accent }}>Rail to EMA</div>
                <div style={{ fontSize: 18, fontWeight: 800, color: "#0f172a" }}>Gateway stations</div>
              </div>
              <span style={opsBadge(primaryRailRows.some((row) => row.status === "red") ? "red" : primaryRailRows.some((row) => row.status === "amber") ? "amber" : "green")}>
                {rail.loading ? "Loading" : "Live"}
              </span>
            </div>
            {rail.error ? <div style={{ color: "#b91c1c", fontSize: 13 }}>{rail.error}</div> : null}
            {!rail.error && primaryRailRows.map((row) => (
              <div key={row.label} style={{ border: "1px solid #e2e8f0", borderRadius: 14, padding: "10px 12px", marginBottom: 10 }}>
                <div style={{ display: "flex", justifyContent: "space-between", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
                  <div style={{ fontWeight: 700, color: "#0f172a" }}>{row.label}</div>
                  <span style={opsBadge(row.status)}>{String(row.status || "unknown").toUpperCase()}</span>
                </div>
                <div style={{ marginTop: 6, fontSize: 13, color: "#334155" }}>{row.summary}</div>
                <div style={{ marginTop: 4, fontSize: 13, color: "#64748b" }}>{row.next}</div>
                {railToday?.[row.target_key] ? <div style={{ marginTop: 6, fontSize: 12, color: "#64748b" }}>{formatRailIssueLine(railToday[row.target_key])}</div> : null}
              </div>
            ))}
            {!rail.error && extraRailRows.length ? (
              <details>
                <summary style={{ cursor: "pointer", color: cardTheme.rail.accent, fontWeight: 700 }}>Show Derby / Long Eaton / Nottingham</summary>
                <div style={{ marginTop: 8, display: "grid", gap: 8 }}>
                  {extraRailRows.map((row) => (
                    <div key={row.label} style={{ border: "1px solid #e2e8f0", borderRadius: 14, padding: "10px 12px" }}>
                      <div style={{ display: "flex", justifyContent: "space-between", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
                        <div style={{ fontWeight: 700, color: "#0f172a" }}>{row.label}</div>
                        <span style={opsBadge(row.status)}>{String(row.status || "unknown").toUpperCase()}</span>
                      </div>
                      <div style={{ marginTop: 6, fontSize: 13, color: "#334155" }}>{row.summary}</div>
                      <div style={{ marginTop: 4, fontSize: 13, color: "#64748b" }}>{row.next}</div>
                      {railToday?.[row.target_key] ? <div style={{ marginTop: 6, fontSize: 12, color: "#64748b" }}>{formatRailIssueLine(railToday[row.target_key])}</div> : null}
                    </div>
                  ))}
                </div>
              </details>
            ) : null}
            {!rail.error && !primaryRailRows.length && <div style={{ color: "#64748b", fontSize: 13 }}>No EMA rail board status available yet.</div>}
          </div>

          <div style={opsCardStyle(cardTheme.flights)}>
            <div style={{ display: "flex", justifyContent: "space-between", gap: 10, alignItems: "center", marginBottom: 10 }}>
              <div>
                <div style={{ ...opsMiniLabel, color: cardTheme.flights.accent }}>EMA flights</div>
                <div style={{ fontSize: 18, fontWeight: 800, color: "#0f172a" }}>Airport operations</div>
              </div>
              <span style={opsBadge(flightSnapshot?.summary?.departures?.status_label || flightSnapshot?.summary?.arrivals?.status_label || "unknown")}>
                {flights.loading ? "Loading" : "Live"}
              </span>
            </div>
            {flights.error ? <div style={{ color: "#b91c1c", fontSize: 13 }}>{flights.error}</div> : null}
            {!flights.error && flightSnapshot?.summary ? (
              <div style={{ display: "grid", gap: 10 }}>
                <div style={{ fontSize: 13, color: "#334155" }}>
                  {`Arrivals ${String(flightSnapshot.summary.arrivals?.status_label || "unknown").toUpperCase()} • Delays ${flightSnapshot.summary.arrivals?.delayed || 0} • Cancellations ${flightSnapshot.summary.arrivals?.cancelled || 0}`}
                </div>
                <div style={{ fontSize: 13, color: "#334155" }}>
                  {`Departures ${String(flightSnapshot.summary.departures?.status_label || "unknown").toUpperCase()} • Delays ${flightSnapshot.summary.departures?.delayed || 0} • Cancellations ${flightSnapshot.summary.departures?.cancelled || 0}`}
                </div>
                {weather ? (
                  <div style={{ fontSize: 12, color: "#64748b" }}>
                    {`EMA weather: ${weather.weather_text || "—"} • ${weather.temp_c != null ? `${weather.temp_c.toFixed(1)}C` : "—"} • wind ${weather.wind_mps != null ? `${weather.wind_mps.toFixed(1)}m/s` : "—"}`}
                  </div>
                ) : null}
                <div style={{ border: "1px solid #e2e8f0", borderRadius: 14, padding: "10px 12px" }}>
                  <div style={{ fontWeight: 700, color: "#0f172a" }}>Recent arrival</div>
                  <div style={{ marginTop: 6, fontSize: 13, color: "#334155" }}>
                    {recentArrival ? formatFlightLine(recentArrival, "arrival") : "No recent arrival snapshot"}
                  </div>
                </div>
                <div style={{ border: "1px solid #e2e8f0", borderRadius: 14, padding: "10px 12px" }}>
                  <div style={{ fontWeight: 700, color: "#0f172a" }}>Recent departure</div>
                  <div style={{ marginTop: 6, fontSize: 13, color: "#334155" }}>
                    {recentDeparture ? formatFlightLine(recentDeparture, "departure") : "No recent departure snapshot"}
                  </div>
                </div>
                <details>
                  <summary style={{ cursor: "pointer", color: cardTheme.flights.accent, fontWeight: 700 }}>Show next movements</summary>
                  <div style={{ marginTop: 8, display: "grid", gap: 8 }}>
                    <div style={{ fontSize: 13, color: "#334155" }}>
                      {nextArrival
                        ? `Next arrival: ${formatFlightUpcomingLine(nextArrival, "arrival")}`
                        : fallbackNextArrival
                        ? `Next scheduled arrival: ${formatFlightScheduleLine(fallbackNextArrival, "arrival")}`
                        : "No near-term arrival currently shown."}
                    </div>
                    <div style={{ fontSize: 13, color: "#334155" }}>
                      {nextDeparture
                        ? `Next departure: ${formatFlightUpcomingLine(nextDeparture, "departure")}`
                        : fallbackNextDeparture
                        ? `Next scheduled departure: ${formatFlightScheduleLine(fallbackNextDeparture, "departure")}`
                        : "No near-term departure currently shown."}
                    </div>
                    {flightSnapshot?.today?.next_delayed ? (
                      <div style={{ fontSize: 12, color: "#64748b" }}>
                        {`Next delayed today: ${formatFlightLine(
                          flightSnapshot.today.next_delayed,
                          String(flightSnapshot.today.next_delayed.flight_direction || "").toLowerCase() === "arrival" ? "arrival" : "departure"
                        )}`}
                      </div>
                    ) : null}
                    {flightSnapshot?.today?.latest_delayed ? (
                      <div style={{ fontSize: 12, color: "#64748b" }}>
                        {`Latest delay seen today: ${formatFlightLine(
                          flightSnapshot.today.latest_delayed,
                          String(flightSnapshot.today.latest_delayed.flight_direction || "").toLowerCase() === "arrival" ? "arrival" : "departure"
                        )}`}
                      </div>
                    ) : null}
                    {flightSnapshot?.today?.latest_cancelled ? (
                      <div style={{ fontSize: 12, color: "#64748b" }}>
                        {`Latest cancelled today: ${formatFlightLine(
                          flightSnapshot.today.latest_cancelled,
                          String(flightSnapshot.today.latest_cancelled.flight_direction || "").toLowerCase() === "arrival" ? "arrival" : "departure"
                        )}`}
                      </div>
                    ) : null}
                  </div>
                </details>
                <div style={{ fontSize: 12, color: "#64748b" }}>
                  Last updated: {flightSnapshot.updated_at ? `${formatDate(flightSnapshot.updated_at)} ${formatTime(flightSnapshot.updated_at)}` : "—"}
                </div>
              </div>
            ) : null}
          </div>
        </div>
      </div>
    </section>
  );
}
